import { MODULES_WITH_COVER_FALLBACKS } from "@utils/constants";
import { StrategyExhibit, StrategyModule } from "@utils/types";
import { assign, setup } from "xstate";

// Helper function to check if module type is in the cover fallbacks list
function moduleHasCoverFallback(moduleType: string): boolean {
  return (MODULES_WITH_COVER_FALLBACKS as readonly string[]).includes(
    moduleType
  );
}

export interface OnboardingContext {
  modules: StrategyModule[];
  currentModuleIndex: number;
  currentExhibitIndex: number;
  currentStep: number;
  totalSteps: number;
  moduleProgress: number;
  completedModules: StrategyModule[];
  followUpTasks: { exhibitId: string; message: string; status: string }[];
}

function updateUrl(module?: StrategyModule, exhibit?: StrategyExhibit) {
  const path = `/onboarding/beneficiary/${module?.type
    ?.toLowerCase()
    .replace(/_/g, "-")}`;
  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.delete("id");
  urlSearchParams.delete("exhibitId");
  if (module) {
    urlSearchParams.set("id", module.id);
  }
  if (exhibit) {
    urlSearchParams.set("exhibitId", exhibit.id);
  }
  window.history.pushState({}, "", `${path}?${urlSearchParams.toString()}`);
}

// Helper function to filter out MEMO_STARTER modules
function getFilteredModules(modules: StrategyModule[]): StrategyModule[] {
  return (modules ?? []).filter((module) => module.type !== "MEMO_STARTER");
}

// Helper function to check if a module has actual COVER_PAGE exhibits
function hasActualCoverPageExhibits(module: StrategyModule): boolean {
  const hasCoverPageExhibit = module.exhibits.some(
    (exhibit: StrategyExhibit) => exhibit.type === "COVER_PAGE"
  );
  return hasCoverPageExhibit;
}

// Helper function to determine if a module should use virtual cover page behavior
function shouldUseVirtualCoverPage(module: StrategyModule): boolean {
  const hasFallback = moduleHasCoverFallback(module.type);
  const hasActual = hasActualCoverPageExhibits(module);
  const shouldUseVirtual = hasFallback && !hasActual;
  return shouldUseVirtual;
}

function getFilteredExhibits(exhibits: StrategyExhibit[]): StrategyExhibit[] {
  const filtered = (exhibits ?? [])
    .filter((exhibit) => !exhibit.hidden)
    .filter((exhibit) => exhibit.type !== "COVER_PAGE"); // Always exclude cover pages from step counting
  return filtered;
}

// Helper function to calculate current step number
function calculateCurrentStep(
  currentExhibitIndex: number,
  currentModule?: StrategyModule
): number {
  if (currentExhibitIndex === -1) {
    // On cover page
    const step = 0;
    return step;
  }

  if (!currentModule) {
    const step = currentExhibitIndex + 1;
    return step;
  }

  // Find position within non-cover-page exhibits
  const filteredExhibits = getFilteredExhibits(currentModule?.exhibits);
  const currentExhibit = currentModule?.exhibits[currentExhibitIndex];
  const positionInFiltered = filteredExhibits.indexOf(currentExhibit);
  const step = positionInFiltered >= 0 ? positionInFiltered + 1 : 0;

  return step;
}

// Helper function to calculate module progress percentage
function calculateModuleProgress(
  currentExhibitIndex: number,
  currentModule: StrategyModule
): number {
  // Get non-cover-page exhibits (cover pages never count as steps)
  const filteredExhibits = getFilteredExhibits(currentModule?.exhibits);
  const totalExhibits = filteredExhibits.length;

  if (totalExhibits === 0) {
    // Handle case with no non-cover-page exhibits
    return 100;
  }

  // Calculate step based on position within non-cover-page exhibits
  let adjustedStep = 0;
  if (currentExhibitIndex === -1) {
    // On cover page
    adjustedStep = 0;
  } else {
    // Find position within non-cover-page exhibits
    const currentExhibit = currentModule?.exhibits[currentExhibitIndex];
    const positionInFiltered = filteredExhibits.indexOf(currentExhibit);
    adjustedStep = positionInFiltered >= 0 ? positionInFiltered + 1 : 0;
  }

  const progress =
    totalExhibits > 0 ? Math.round((adjustedStep / totalExhibits) * 100) : 100;

  return progress;
}

// Helper function to calculate total steps for a module
function calculateTotalSteps(currentModule: StrategyModule): number {
  // Get non-cover-page exhibits (cover pages never count as steps)
  const filteredExhibits = getFilteredExhibits(currentModule?.exhibits);
  const totalExhibits = filteredExhibits.length;

  return totalExhibits;
}

type OnboardingEvent =
  | { type: "NEXT_EXHIBIT" }
  | { type: "PREV_EXHIBIT" }
  | { type: "NEXT_MODULE" }
  | { type: "PREV_MODULE" }
  | {
      type: "SET_DATA";
      moduleId: string;
      exhibitId: string;
      status?: StrategyExhibit["status"];
      data: Record<string, unknown>;
    }
  | {
      type: "SET_EXHIBIT_STATUS";
      moduleId: string;
      exhibitId: string;
      status: StrategyExhibit["status"];
    }
  | { type: "VALIDATE" }
  | {
      type: "INIT_MODULES";
      modules: StrategyModule[];
      defaultExhibitIndex: number;
      defaultModuleIndex: number;
    };

function loadInitialContext(): OnboardingContext {
  const viewTeamId = localStorage.getItem("viewTeam");
  const viewAccountId = localStorage.getItem("viewAccountId");
  const savedModules = localStorage.getItem(
    "onboardingState" + viewTeamId + viewAccountId
  );
  const savedCurrentModuleIndex = localStorage.getItem(
    "currentModuleIndex" + viewTeamId + viewAccountId
  );
  const savedCurrentExhibitIndex = localStorage.getItem(
    "currentExhibitIndex" + viewTeamId + viewAccountId
  );

  if (
    savedModules &&
    savedCurrentModuleIndex !== null &&
    savedCurrentExhibitIndex !== null
  ) {
    const parsedModules = JSON.parse(savedModules);
    const currentModuleIndex = parseInt(savedCurrentModuleIndex, 10);
    const currentExhibitIndex = parseInt(savedCurrentExhibitIndex, 10);

    // Check if the current module is one of the special types that should start with -1
    const currentModule = parsedModules[currentModuleIndex];
    const specialModuleTypes = MODULES_WITH_COVER_FALLBACKS;

    // If it's a special module type and the saved index is 0, set it to -1
    const adjustedExhibitIndex =
      currentModule &&
      specialModuleTypes.includes(currentModule.type) &&
      currentExhibitIndex === 0
        ? -1
        : currentExhibitIndex;

    const initialCurrentStep = calculateCurrentStep(
      adjustedExhibitIndex,
      currentModule
    );
    const initialModuleProgress = calculateModuleProgress(
      adjustedExhibitIndex,
      parsedModules[currentModuleIndex]
    );
    const initialTotalSteps = calculateTotalSteps(
      parsedModules[currentModuleIndex]
    );

    return {
      modules: parsedModules,
      currentModuleIndex,
      currentExhibitIndex: adjustedExhibitIndex,
      currentStep: initialCurrentStep,
      totalSteps: initialTotalSteps,
      moduleProgress: initialModuleProgress,
      completedModules: [],
      followUpTasks: [],
    };
  }

  // Default context if no saved state is found
  return {
    modules: [],
    currentModuleIndex: 0,
    currentExhibitIndex: 0,
    currentStep: 0,
    totalSteps: 0,
    moduleProgress: 0,
    completedModules: [],
    followUpTasks: [],
  };
}

export const onboardingMachine = setup({
  types: {
    context: {} as OnboardingContext,
    events: {} as OnboardingEvent,
  },
  actions: {
    initializeModules: assign({
      modules: ({ event }) =>
        event.type === "INIT_MODULES" ? event.modules : [],
      currentModuleIndex: ({ event }) =>
        event.type === "INIT_MODULES" ? event.defaultModuleIndex : 0,
      currentExhibitIndex: ({ event }) => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const exhibitId = urlSearchParams.get("exhibitId");
        if (event.type === "INIT_MODULES") {
          // If it's a special module type and doesn't already have a COVER_PAGE exhibit, set the index to -1
          const filteredModules = getFilteredModules(event.modules);
          const currentFilteredIndex = filteredModules.indexOf(
            event.modules[event.defaultModuleIndex]
          );
          const currentModule = filteredModules[currentFilteredIndex];

          // Use virtual cover page behavior if module should have cover fallback but no actual cover page
          const shouldStartWithVirtualCoverPage =
            currentModule && shouldUseVirtualCoverPage(currentModule);

          // Use actual cover page if module has real cover page exhibits
          const hasRealCoverPage =
            currentModule && hasActualCoverPageExhibits(currentModule);

          const result =
            currentModule &&
            (shouldStartWithVirtualCoverPage || hasRealCoverPage) &&
            !exhibitId
              ? hasRealCoverPage
                ? 0
                : -1 // Real cover page at index 0, virtual at -1
              : event.defaultExhibitIndex;

          return result;
        }
        return 0;
      },
      totalSteps: ({ event }) => {
        if (event.type === "INIT_MODULES") {
          const currentModule = event.modules[event.defaultModuleIndex];
          return calculateTotalSteps(currentModule);
        }
        return 0;
      },
      currentStep: ({ event }) => {
        if (event.type === "INIT_MODULES") {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const exhibitId = urlSearchParams.get("exhibitId");
          let exhibitIndex = event.defaultExhibitIndex;
          const filteredModules = getFilteredModules(event.modules);
          const currentFilteredIndex = filteredModules.indexOf(
            event.modules[event.defaultModuleIndex]
          );
          const currentModule = filteredModules[currentFilteredIndex];
          // Check for virtual or actual cover page behavior
          const shouldStartWithVirtualCoverPage =
            shouldUseVirtualCoverPage(currentModule);
          const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);

          // If starting with cover page behavior and no specific exhibit
          if (
            (shouldStartWithVirtualCoverPage || hasRealCoverPage) &&
            !exhibitId
          ) {
            exhibitIndex = hasRealCoverPage ? 0 : -1; // Real cover page at index 0, virtual at -1
          }

          return calculateCurrentStep(exhibitIndex, currentModule);
        }
        return 0;
      },
      moduleProgress: ({ event }) => {
        if (event.type === "INIT_MODULES") {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const exhibitId = urlSearchParams.get("exhibitId");
          const currentModule = event.modules[event.defaultModuleIndex];
          let exhibitIndex = event.defaultExhibitIndex;

          // Check for virtual or actual cover page behavior
          const shouldStartWithVirtualCoverPage =
            currentModule && shouldUseVirtualCoverPage(currentModule);
          const hasRealCoverPage =
            currentModule && hasActualCoverPageExhibits(currentModule);

          // If starting with cover page behavior and no specific exhibit
          if (
            (shouldStartWithVirtualCoverPage || hasRealCoverPage) &&
            !exhibitId
          ) {
            exhibitIndex = hasRealCoverPage ? 0 : -1; // Real cover page at index 0, virtual at -1
          }

          return calculateModuleProgress(exhibitIndex, currentModule);
        }
        return 0;
      },
      completedModules: () => [],
      followUpTasks: () => [],
    }),
    setExhibitData: assign({
      modules: ({ context, event }) => {
        if (event.type === "SET_DATA") {
          const { moduleId, exhibitId, data, status } = event;
          return context.modules.map((module) =>
            module.id === moduleId
              ? {
                  ...module,
                  status:
                    module.status === "pending" ? "in_progress" : module.status,
                  exhibits: module.exhibits.map((exhibit: StrategyExhibit) =>
                    exhibit.id === exhibitId
                      ? {
                          ...exhibit,
                          data,
                          status: status ?? ("in_progress" as const),
                        }
                      : exhibit
                  ),
                }
              : module
          );
        }
        return context.modules;
      },
    }),
    setExhibitStatus: assign({
      modules: ({ context, event }) => {
        if (event.type === "SET_EXHIBIT_STATUS") {
          const { moduleId, exhibitId, status } = event;
          return context.modules.map((module) =>
            module.id === moduleId
              ? {
                  ...module,
                  status:
                    module.status === "pending" ? "in_progress" : module.status,
                  exhibits: module.exhibits.map((exhibit: StrategyExhibit) =>
                    exhibit.id === exhibitId
                      ? { ...exhibit, status: status }
                      : exhibit
                  ),
                }
              : module
          );
        }
        return context.modules;
      },
    }),
    nextExhibit: assign({
      currentExhibitIndex: ({ context }) => {
        const unFilteredExhibits =
          context.modules[context.currentModuleIndex].exhibits;

        const currentModule = context.modules[context.currentModuleIndex];
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const filteredExhibits = getFilteredExhibits(
          context.modules[context.currentModuleIndex].exhibits
        );

        // If we're at -1 (virtual cover page), move to first non-cover-page exhibit
        if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
          const result =
            filteredExhibits.length > 0
              ? unFilteredExhibits.indexOf(filteredExhibits[0])
              : 0;
          return result;
        }

        // If we're at index 0 and it's a real cover page, move to first non-cover-page exhibit
        if (context.currentExhibitIndex === 0 && hasRealCoverPage) {
          const currentExhibit =
            unFilteredExhibits[context.currentExhibitIndex];
          if (currentExhibit?.type === "COVER_PAGE") {
            const result =
              filteredExhibits.length > 0
                ? unFilteredExhibits.indexOf(filteredExhibits[0])
                : 1;
            return result;
          }
        }

        const nextIndex = context.currentExhibitIndex;
        // find the next non filtered exhibit starting from the next index
        const nextNonFilteredExhibitIndex = unFilteredExhibits.findIndex(
          (exhibit: StrategyExhibit, index: number) =>
            index > nextIndex && filteredExhibits.includes(exhibit)
        );

        if (nextNonFilteredExhibitIndex === -1) {
          return context.currentExhibitIndex;
        }

        return nextNonFilteredExhibitIndex;
      },
      currentStep: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        const unFilteredExhibits = currentModule.exhibits;
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const filteredExhibits = getFilteredExhibits(unFilteredExhibits);

        // If we're at -1 (virtual cover page), next step should be 1
        if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
          return 1; // First non-cover-page exhibit step should be 1
        }

        // If we're at index 0 and it's a real cover page, next step should be 1
        if (context.currentExhibitIndex === 0 && hasRealCoverPage) {
          const currentExhibit =
            unFilteredExhibits[context.currentExhibitIndex];
          if (currentExhibit?.type === "COVER_PAGE") {
            return 1; // First non-cover-page exhibit step should be 1
          }
        }

        // Find next non-filtered exhibit and calculate its step
        const nextNonFilteredExhibitIndex = unFilteredExhibits.findIndex(
          (exhibit: StrategyExhibit, index: number) =>
            index > context.currentExhibitIndex &&
            filteredExhibits.includes(exhibit)
        );

        if (nextNonFilteredExhibitIndex === -1) {
          return calculateCurrentStep(
            context.currentExhibitIndex,
            currentModule
          );
        }

        return calculateCurrentStep(nextNonFilteredExhibitIndex, currentModule);
      },
      totalSteps: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        return calculateTotalSteps(currentModule);
      },
      moduleProgress: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        // Calculate next exhibit index
        let nextExhibitIndex;
        if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
          // Moving from virtual cover page to first non-cover-page exhibit
          const filteredExhibits = getFilteredExhibits(currentModule.exhibits);
          nextExhibitIndex =
            filteredExhibits.length > 0
              ? currentModule.exhibits.indexOf(filteredExhibits[0])
              : 0;
        } else if (context.currentExhibitIndex === 0 && hasRealCoverPage) {
          // Check if we're on a real cover page
          const currentExhibit =
            currentModule.exhibits[context.currentExhibitIndex];
          if (currentExhibit?.type === "COVER_PAGE") {
            // Moving from real cover page to first non-cover-page exhibit
            const filteredExhibits = getFilteredExhibits(
              currentModule.exhibits
            );
            nextExhibitIndex =
              filteredExhibits.length > 0
                ? currentModule.exhibits.indexOf(filteredExhibits[0])
                : 1;
          } else {
            nextExhibitIndex = context.currentExhibitIndex + 1;
          }
        } else {
          // Standard progression
          nextExhibitIndex = context.currentExhibitIndex + 1;
        }

        return calculateModuleProgress(nextExhibitIndex, currentModule);
      },
    }),
    prevExhibit: assign({
      currentExhibitIndex: ({ context }) => {
        const unFilteredExhibits =
          context.modules[context.currentModuleIndex].exhibits;

        const currentModule = context.modules[context.currentModuleIndex];
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const filteredExhibits = getFilteredExhibits(
          context.modules[context.currentModuleIndex].exhibits
        );

        // If we're at -1 (cover page), we can't go back further within this module
        if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
          return -1;
        }

        // find the previous non filtered exhibit starting from the previous index starting from  the end of the array
        const previousNonFilteredExhibitIndex =
          unFilteredExhibits.findLastIndex(
            (exhibit: StrategyExhibit, index: number) =>
              index < context.currentExhibitIndex &&
              filteredExhibits.includes(exhibit)
          );

        if (previousNonFilteredExhibitIndex === -1) {
          // If no previous exhibit found and module has cover page, go to cover page
          if (hasAnyCoverPage) {
            // For modules with real cover pages, go to index 0 (actual cover page)
            // For modules with virtual cover pages only, go to index -1
            if (hasRealCoverPage) {
              return 0;
            } else {
              return -1;
            }
          }
          return 0;
        }

        const lowerBound = hasAnyCoverPage ? -1 : 0;
        const result = Math.max(previousNonFilteredExhibitIndex, lowerBound);
        return result;
      },
      currentStep: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        const unFilteredExhibits = currentModule.exhibits;
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const filteredExhibits = getFilteredExhibits(unFilteredExhibits);

        // Handle special case for cover page modules
        if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
          return 0;
        }

        // Find previous non-filtered exhibit
        const prevNonFilteredExhibitIndex = unFilteredExhibits.findLastIndex(
          (exhibit: StrategyExhibit, index: number) =>
            index < context.currentExhibitIndex &&
            filteredExhibits.includes(exhibit)
        );

        if (prevNonFilteredExhibitIndex === -1) {
          // If no previous exhibit found and module has cover page, go to cover page
          if (hasAnyCoverPage) {
            return 0; // Cover page step
          }
          return calculateCurrentStep(
            context.currentExhibitIndex,
            currentModule
          );
        }

        return calculateCurrentStep(prevNonFilteredExhibitIndex, currentModule);
      },
      totalSteps: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        return calculateTotalSteps(currentModule);
      },
      moduleProgress: ({ context }) => {
        const currentModule = context.modules[context.currentModuleIndex];
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const lowerBound = hasAnyCoverPage ? -1 : 0;
        const prevExhibitIndex = Math.max(
          context.currentExhibitIndex - 1,
          lowerBound
        );

        return calculateModuleProgress(prevExhibitIndex, currentModule);
      },
    }),
    completeModule: assign({
      modules: ({ context }) => {
        return context.modules.map((module, index) =>
          index === context.currentModuleIndex
            ? {
                ...module,
                status: module.exhibits.every((ex: StrategyExhibit) =>
                  ["completed", "skipped"].includes(ex.status)
                )
                  ? ("completed" as const)
                  : ("in_progress" as const),
              }
            : module
        );
      },
      completedModules: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        return [
          ...context.completedModules,
          filteredModules[context.currentModuleIndex],
        ];
      },
      currentExhibitIndex: () => 0,
    }),
    nextModule: assign({
      currentModuleIndex: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const nextModule = filteredModules[currentFilteredIndex + 1];
        return context.modules.indexOf(nextModule);
      },
      currentExhibitIndex: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const nextModule = filteredModules[currentFilteredIndex + 1];

        // Check if this module should start with a cover page
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(nextModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(nextModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        if (hasAnyCoverPage) {
          // Start with cover page - real cover page at index 0, virtual at -1
          return hasRealCoverPage ? 0 : -1;
        } else {
          // Start with first non-cover-page exhibit
          const filteredExhibits = getFilteredExhibits(nextModule.exhibits);
          if (filteredExhibits.length > 0) {
            // Find the index of the first filtered exhibit in the original exhibits array
            return nextModule.exhibits.indexOf(filteredExhibits[0]);
          }
          return 0;
        }
      },
      currentStep: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const nextModule = filteredModules[currentFilteredIndex + 1];

        // Check if this module should start with a cover page
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(nextModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(nextModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const nextExhibitIndex = hasAnyCoverPage
          ? hasRealCoverPage
            ? 0
            : -1
          : 0;
        return calculateCurrentStep(nextExhibitIndex, nextModule);
      },
      totalSteps: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const nextModule = filteredModules[currentFilteredIndex + 1];
        return calculateTotalSteps(nextModule);
      },
      moduleProgress: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const nextModule = filteredModules[currentFilteredIndex + 1];

        // Check if this module should start with a cover page
        const hasVirtualCoverPage = shouldUseVirtualCoverPage(nextModule);
        const hasRealCoverPage = hasActualCoverPageExhibits(nextModule);
        const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

        const nextExhibitIndex = hasAnyCoverPage
          ? hasRealCoverPage
            ? 0
            : -1
          : 0;
        return calculateModuleProgress(nextExhibitIndex, nextModule);
      },
    }),
    prevModule: assign({
      currentModuleIndex: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const prevModule = filteredModules[currentFilteredIndex - 1];
        const prevModuleIndex = context.modules.indexOf(prevModule);

        return prevModuleIndex;
      },
      currentExhibitIndex: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const previousModule = filteredModules[currentFilteredIndex - 1];

        // Go to the last non-cover-page exhibit of the previous module
        const filteredExhibits = getFilteredExhibits(previousModule.exhibits);
        if (filteredExhibits.length > 0) {
          const lastFilteredExhibit =
            filteredExhibits[filteredExhibits.length - 1];
          const lastExhibitIndex =
            previousModule.exhibits.indexOf(lastFilteredExhibit);

          // Find the index of this exhibit in the original exhibits array
          return lastExhibitIndex;
        }

        // If no filtered exhibits, default to first exhibit
        return 0;
      },
      currentStep: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const prevModule = filteredModules[currentFilteredIndex - 1];

        // Calculate step for the last non-cover-page exhibit
        const filteredExhibits = getFilteredExhibits(prevModule.exhibits);
        if (filteredExhibits.length > 0) {
          const lastFilteredExhibit =
            filteredExhibits[filteredExhibits.length - 1];
          const lastExhibitIndex =
            prevModule.exhibits.indexOf(lastFilteredExhibit);
          return calculateCurrentStep(lastExhibitIndex, prevModule);
        }

        return 0;
      },
      moduleProgress: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const prevModule = filteredModules[currentFilteredIndex - 1];

        // Calculate progress for the last non-cover-page exhibit (should be 100%)
        const filteredExhibits = getFilteredExhibits(prevModule.exhibits);
        if (filteredExhibits.length > 0) {
          const lastFilteredExhibit =
            filteredExhibits[filteredExhibits.length - 1];
          const lastExhibitIndex =
            prevModule.exhibits.indexOf(lastFilteredExhibit);
          return calculateModuleProgress(lastExhibitIndex, prevModule);
        }

        return 100; // Default to 100% if no exhibits
      },
      totalSteps: ({ context }) => {
        const filteredModules = getFilteredModules(context.modules);
        const currentFilteredIndex = filteredModules.indexOf(
          context.modules[context.currentModuleIndex]
        );
        const prevModule = filteredModules[currentFilteredIndex - 1];
        return calculateTotalSteps(prevModule);
      },
    }),
    saveToLocalStorage: ({ context }) => {
      const viewTeamId = localStorage.getItem("viewTeam");
      const viewAccountId = localStorage.getItem("viewAccountId");
      localStorage.setItem(
        "onboardingState" + viewTeamId + viewAccountId,
        JSON.stringify(context.modules)
      );
      localStorage.setItem(
        "currentModuleIndex" + viewTeamId + viewAccountId,
        context.currentModuleIndex.toString()
      );
      localStorage.setItem(
        "currentExhibitIndex" + viewTeamId + viewAccountId,
        context.currentExhibitIndex.toString()
      );
    },
    updateUrl: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const currentExhibit =
        currentModule.exhibits[context.currentExhibitIndex];

      updateUrl(currentModule, currentExhibit);
    },
    validateExhibits: assign({
      followUpTasks: ({ context }) => {
        const modules = getFilteredModules(context.modules);

        const tasks = [];
        for (let i = 0; i < modules.length; i++) {
          const module = modules[i];
          const filteredExhibits = getFilteredExhibits(module.exhibits);
          for (let j = 0; j < filteredExhibits.length; j++) {
            const exhibit = filteredExhibits[j];
            if (
              exhibit.required &&
              ["pending", "in_progress"].includes(exhibit.status)
            ) {
              tasks.push({
                exhibitId: exhibit.id,
                moduleId: module.id,
                moduleIndex: i,
                exhibitIndex: j,
                message: `Please provide the required data for ${exhibit.name}: ${exhibit.description}`,
                status: "pending",
              });
            }
          }
        }
        return tasks;
      },
    }),
  },
  guards: {
    hasMoreExhibitsInModule: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
      const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
      const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

      const filteredExhibits = getFilteredExhibits(currentModule.exhibits);

      // If we're on the virtual cover page (-1), there are always more exhibits to go to
      if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
        return filteredExhibits.length > 0;
      }

      // If we're on a real cover page (index 0), check if there are non-cover-page exhibits to go to
      if (context.currentExhibitIndex === 0 && hasRealCoverPage) {
        const currentExhibit =
          currentModule.exhibits[context.currentExhibitIndex];
        if (currentExhibit?.type === "COVER_PAGE") {
          return filteredExhibits.length > 0;
        }
      }

      // If current exhibit is not in valid range, assume no more exhibits
      if (
        context.currentExhibitIndex < 0 ||
        context.currentExhibitIndex >= currentModule.exhibits.length
      ) {
        return false;
      }

      const currentExhibit =
        currentModule.exhibits[context.currentExhibitIndex];
      const filteredExhibitIndex = filteredExhibits.indexOf(currentExhibit);

      // If not found in filtered exhibits, assume no more exhibits
      if (filteredExhibitIndex === -1) {
        return false;
      }

      const hasMore = filteredExhibitIndex < filteredExhibits.length - 1;
      return hasMore;
    },
    isLastExhibitInModule: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
      const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
      const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

      const filteredExhibits = getFilteredExhibits(currentModule.exhibits);

      // If we're on the cover page (-1), we're definitely not on the last exhibit
      if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
        return false;
      }

      // If current exhibit is not in valid range, it's not the last exhibit
      if (
        context.currentExhibitIndex < 0 ||
        context.currentExhibitIndex >= currentModule.exhibits.length
      ) {
        return false;
      }

      const currentExhibit =
        currentModule.exhibits[context.currentExhibitIndex];
      const filteredExhibitIndex = filteredExhibits.indexOf(currentExhibit);

      // If not found in filtered exhibits, it's not the last exhibit
      if (filteredExhibitIndex === -1) {
        return false;
      }

      return filteredExhibitIndex === filteredExhibits.length - 1;
    },
    notFirstExhibitInModule: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const hasVirtualCoverPage = shouldUseVirtualCoverPage(currentModule);
      const hasRealCoverPage = hasActualCoverPageExhibits(currentModule);
      const hasAnyCoverPage = hasVirtualCoverPage || hasRealCoverPage;

      // If we're on the cover page (-1), we're at the beginning, can't go back further within module
      if (context.currentExhibitIndex === -1 && hasAnyCoverPage) {
        return false;
      }

      // If we're on a real cover page (index 0), we're at the beginning, can't go back further within module
      if (context.currentExhibitIndex === 0 && hasRealCoverPage) {
        const currentExhibit =
          currentModule.exhibits[context.currentExhibitIndex];
        if (currentExhibit?.type === "COVER_PAGE") {
          return false;
        }
      }

      // If there's any cover page behavior, we can always go back to it from any non-cover-page exhibit
      if (hasAnyCoverPage && context.currentExhibitIndex >= 0) {
        const currentExhibit =
          currentModule.exhibits[context.currentExhibitIndex];
        const isCurrentExhibitCoverPage = currentExhibit?.type === "COVER_PAGE";

        if (!isCurrentExhibitCoverPage) {
          // We're on a non-cover-page exhibit and there's cover page behavior, so we can go back to cover page
          return true;
        }
      }

      // No cover page behavior - use standard filtered exhibit logic
      const filteredExhibits = getFilteredExhibits(currentModule.exhibits);

      // If current exhibit is not in valid range, can't go back
      if (
        context.currentExhibitIndex < 0 ||
        context.currentExhibitIndex >= currentModule.exhibits.length
      ) {
        return false;
      }

      const currentExhibit =
        currentModule.exhibits[context.currentExhibitIndex];
      const filteredExhibitIndex = filteredExhibits.indexOf(currentExhibit);

      // If not found in filtered exhibits, treat as first
      if (filteredExhibitIndex === -1) {
        return false;
      }

      const canGoBack = filteredExhibitIndex !== 0;
      return canGoBack;
    },
    hasMoreModules: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const filteredModules = getFilteredModules(context.modules);
      const filteredModuleIndex = filteredModules.indexOf(currentModule);

      return filteredModuleIndex < filteredModules.length - 1;
    },
    notFirstModule: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];
      const filteredModules = getFilteredModules(context.modules);
      const filteredModuleIndex = filteredModules.indexOf(currentModule);

      return filteredModuleIndex > 0;
    },
    hasFollowupTasks: ({ context }) => {
      const currentModule = context.modules[context.currentModuleIndex];

      const filteredExhibits = getFilteredExhibits(currentModule.exhibits);

      return filteredExhibits.some((ex: StrategyExhibit) =>
        ["pending", "in_progress"].includes(ex.status)
      );
    },
  },
}).createMachine({
  id: "onboarding",
  initial: "initializing",
  context: ({ input }) => ({
    ...loadInitialContext(),
    ...input,
  }),
  states: {
    initializing: {
      on: {
        INIT_MODULES: {
          target: "collectingData",
          actions: ["initializeModules", "saveToLocalStorage"],
        },
      },
    },
    collectingData: {
      on: {
        SET_DATA: {
          actions: ["setExhibitData", "saveToLocalStorage"],
        },
        SET_EXHIBIT_STATUS: {
          actions: ["setExhibitStatus", "saveToLocalStorage"],
        },
        NEXT_EXHIBIT: [
          {
            target: "collectingData",
            actions: ["nextExhibit", "saveToLocalStorage", "updateUrl"],
            guard: "hasMoreExhibitsInModule",
          },
          {
            target: "nextModule",
            actions: ["completeModule", "saveToLocalStorage"],
            guard: "isLastExhibitInModule",
          },
        ],
        PREV_EXHIBIT: [
          {
            target: "collectingData",
            actions: ["prevExhibit", "saveToLocalStorage", "updateUrl"],
            guard: "notFirstExhibitInModule",
          },
          {
            target: "previousModule",
            actions: ["saveToLocalStorage", "updateUrl"],
            guard: "notFirstModule",
          },
        ],
        VALIDATE: {
          actions: ["validateExhibits", "saveToLocalStorage"],
          target: "validation",
        },
      },
    },
    previousModule: {
      always: [
        {
          target: "collectingData",
          actions: ["prevModule", "saveToLocalStorage", "updateUrl"],
          guard: "notFirstModule",
        },
      ],
    },
    nextModule: {
      always: [
        {
          target: "collectingData",
          actions: ["nextModule", "saveToLocalStorage", "updateUrl"],
          guard: "hasMoreModules",
        },
        {
          target: "followup",
          actions: ["completeModule"],
          guard: "hasFollowupTasks",
        },
        {
          target: "completed",
          actions: ["completeModule"],
        },
      ],
    },
    validation: {
      always: {
        target: "collectingData",
        actions: [
          "saveToLocalStorage",
          ({ context }) => {
            if (context.followUpTasks.length > 0) {
              console.log("Follow-up tasks needed:", context.followUpTasks);
            } else {
              console.log("All required data is complete");
            }
          },
        ],
      },
    },
    followup: {
      type: "final",
    },
    completed: {
      type: "final",
    },
  },
});
